import React, { useState } from 'react';
import { useAccount, useSwitchChain } from 'wagmi';
import { AlertTriangle, RefreshCw, CheckCircle } from 'lucide-react';
import { colors, spacing, typography, borders, transitions } from '../../tokens';
import { getChainInfo } from '../../web3/config';
import { bsc } from 'wagmi/chains';

export interface NetworkGuardProps {
  /**
   * Required chain ID (default: BSC Mainnet = 56)
   */
  requiredChainId?: number;
  /**
   * Children to render when on correct network
   */
  children: React.ReactNode;
  /**
   * Show compact version
   */
  compact?: boolean;
  /**
   * Override display name for the required network
   * (e.g., show "BSC Mainnet" even when requiring Sepolia in dev mode)
   */
  displayName?: string;
}

/**
 * NetworkGuard - Force Users to Switch to Required Network
 *
 * Features:
 * - Detect current network
 * - Show warning if on wrong network
 * - One-click network switch
 * - Display required network info
 *
 * Usage:
 * ```tsx
 * <NetworkGuard requiredChainId={56}>
 *   <YourContent />
 * </NetworkGuard>
 * ```
 */
export const NetworkGuard: React.FC<NetworkGuardProps> = ({
  requiredChainId = bsc.id, // BSC Mainnet = 56
  children,
  compact = false,
  displayName,
}) => {
  const { chainId, isConnected } = useAccount();
  const { switchChain, isPending } = useSwitchChain();

  const [isHovered, setIsHovered] = useState(false);

  const requiredChainInfo = getChainInfo(requiredChainId);
  const currentChainInfo = chainId ? getChainInfo(chainId) : null;

  // Use displayName override if provided, otherwise use chain metadata name
  const networkDisplayName = displayName || requiredChainInfo.name;

  // If not connected, don't show guard
  if (!isConnected) {
    return null;
  }

  // If on correct network, render children
  if (chainId === requiredChainId) {
    return <>{children}</>;
  }

  // Handle switch network
  const handleSwitch = async () => {
    try {
      await switchChain({ chainId: requiredChainId });
    } catch (error) {
      console.error('Failed to switch network:', error);
    }
  };

  // Warning container styles
  const containerStyles: React.CSSProperties = {
    backgroundColor: `${colors.semantic.warning}10`,
    border: `2px solid ${colors.semantic.warning}`,
    borderRadius: borders.component.card.radius,
    padding: compact ? spacing.lg : spacing.xl,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: spacing.lg,
    textAlign: 'center',
  };

  // Icon container styles
  const iconContainerStyles: React.CSSProperties = {
    width: compact ? '48px' : '64px',
    height: compact ? '48px' : '64px',
    borderRadius: '50%',
    backgroundColor: colors.semantic.warning,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  // Title styles
  const titleStyles: React.CSSProperties = {
    fontSize: compact ? typography.fontSize.lg : typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.bold,
    color: colors.text.primary.default,
    margin: 0,
  };

  // Description styles
  const descriptionStyles: React.CSSProperties = {
    fontSize: typography.fontSize.base,
    color: colors.text.secondary.default,
    maxWidth: '500px',
    margin: 0,
  };

  // Network info styles
  const networkInfoStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.lg,
    marginTop: spacing.md,
    flexWrap: 'wrap',
  };

  const networkBadgeStyles: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`,
    borderRadius: borders.component.default.radius,
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
  };

  const currentNetworkStyles: React.CSSProperties = {
    ...networkBadgeStyles,
    backgroundColor: colors.semantic.error,
    color: colors.neutral.white,
  };

  const requiredNetworkStyles: React.CSSProperties = {
    ...networkBadgeStyles,
    backgroundColor: colors.semantic.success,
    color: colors.neutral.white,
  };

  // Button styles
  const buttonStyles: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing.sm,
    padding: `${spacing.md} ${spacing.xl}`,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    backgroundColor: colors.brand.primary.base,
    color: colors.neutral.white,
    border: 'none',
    borderRadius: borders.component.button.radius,
    cursor: isPending ? 'not-allowed' : 'pointer',
    transition: transitions.default,
    fontFamily: typography.fontFamily.sans,
    opacity: isPending ? 0.6 : 1,
    ...(isHovered && !isPending && {
      backgroundColor: colors.brand.primary.dark,
      transform: 'translateY(-2px)',
    }),
  };

  return (
    <div style={containerStyles}>
      <div style={iconContainerStyles}>
        <AlertTriangle size={compact ? 28 : 36} color={colors.neutral.white} />
      </div>

      <div>
        <h3 style={titleStyles}>Wrong Network Detected</h3>
        <p style={descriptionStyles}>
          Please switch to <strong>{networkDisplayName}</strong> to continue.
          This application only works on {networkDisplayName}.
        </p>
      </div>

      <div style={networkInfoStyles}>
        {currentChainInfo && (
          <div style={currentNetworkStyles}>
            Current: {currentChainInfo.name}
          </div>
        )}
        <div style={requiredNetworkStyles}>
          <CheckCircle size={16} style={{ display: 'inline', marginRight: spacing.xs }} />
          Required: {networkDisplayName}
        </div>
      </div>

      <button
        style={buttonStyles}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleSwitch}
        disabled={isPending}
      >
        {isPending ? (
          <>
            <RefreshCw size={20} className="animate-spin" />
            <span>Switching Network...</span>
          </>
        ) : (
          <>
            <RefreshCw size={20} />
            <span>Switch to {networkDisplayName}</span>
          </>
        )}
      </button>

      {!compact && (
        <div style={{
          fontSize: typography.fontSize.sm,
          color: colors.text.muted.default,
          marginTop: spacing.md,
        }}>
          Your wallet will prompt you to approve the network switch
        </div>
      )}
    </div>
  );
};

export default NetworkGuard;
